import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Sparkles, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const Demo = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    intentionalOrientation: '',
    culturalReferences: [],
    sustainabilityPriority: [3],
    climateContext: '',
    communicationGoal: ''
  });

  const handleOrientationChange = (value) => {
    setFormData({ ...formData, intentionalOrientation: value });
  };

  const handleCulturalReferenceToggle = (reference) => {
    const current = formData.culturalReferences;
    const updated = current.includes(reference)
      ? current.filter(r => r !== reference)
      : [...current, reference];
    setFormData({ ...formData, culturalReferences: updated });
  };

  const handleSustainabilityChange = (value) => {
    setFormData({ ...formData, sustainabilityPriority: value });
  };

  const handleClimateChange = (value) => {
    setFormData({ ...formData, climateContext: value });
  };

  const handleCommunicationChange = (e) => {
    setFormData({ ...formData, communicationGoal: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.intentionalOrientation || !formData.climateContext || !formData.communicationGoal) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Navigate to results page with form data
    navigate('/demo/results', { state: { formData } });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FAF9F7]/95 backdrop-blur-sm border-b border-[#E8C4AD]">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-serif font-bold text-[#D47B5E]">
              FashionClarity
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <Link to="/">
                <Button variant="ghost" className="text-[#D47B5E]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-[#D47B5E] hover:bg-[#F9E5D9] transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#E8C4AD]">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="text-[#D47B5E] w-full justify-start">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </Link>
            </div>
          )}
        </nav>
      </header>

      {/* Demo Form */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-[#E8B298] mr-3" />
              <h1 className="text-4xl font-serif font-light text-[#D47B5E]">
                Design Brief Generator
              </h1>
            </div>
            <p className="text-lg text-[#2C2C2C]">
              Answer a few questions to receive your structured design brief
            </p>
          </div>

          <Card className="bg-white border-[#E8C4AD] shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-[#D47B5E]">User Profiling Form</CardTitle>
              <CardDescription>Tell us about your fashion needs and preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Intentional Orientation */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-[#D47B5E]">
                    Intentional Orientation *
                  </Label>
                  <p className="text-sm text-[#B8927C]">What do you want your outfit to convey?</p>
                  <RadioGroup value={formData.intentionalOrientation} onValueChange={handleOrientationChange}>
                    <div className="flex items-center space-x-2 py-2">
                      <RadioGroupItem value="professional-authority" id="professional" />
                      <Label htmlFor="professional" className="font-normal cursor-pointer">Professional authority</Label>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                      <RadioGroupItem value="cultural-celebration" id="cultural" />
                      <Label htmlFor="cultural" className="font-normal cursor-pointer">Cultural celebration</Label>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                      <RadioGroupItem value="subtle-confidence" id="subtle" />
                      <Label htmlFor="subtle" className="font-normal cursor-pointer">Subtle confidence</Label>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                      <RadioGroupItem value="visible-statement" id="visible" />
                      <Label htmlFor="visible" className="font-normal cursor-pointer">Visible statement</Label>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                      <RadioGroupItem value="ceremonial" id="ceremonial" />
                      <Label htmlFor="ceremonial" className="font-normal cursor-pointer">Ceremonial</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Cultural Reference */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-[#D47B5E]">
                    Cultural Reference
                  </Label>
                  <p className="text-sm text-[#B8927C]">Select all that apply</p>
                  <div className="space-y-3">
                    {[
                      { id: 'yoruba', label: 'Yoruba (Aso Oke)' },
                      { id: 'igbo', label: 'Igbo (George)' },
                      { id: 'ghanaian', label: 'Ghanaian (Kente)' },
                      { id: 'contemporary', label: 'Contemporary African fusion' },
                      { id: 'none', label: 'No cultural preference' }
                    ].map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={option.id}
                          checked={formData.culturalReferences.includes(option.id)}
                          onCheckedChange={() => handleCulturalReferenceToggle(option.id)}
                        />
                        <Label htmlFor={option.id} className="font-normal cursor-pointer">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sustainability Priority */}
                <div className="space-y-4">
                  <Label className="text-lg font-semibold text-[#D47B5E]">
                    Sustainability Priority
                  </Label>
                  <div className="flex justify-between text-sm text-[#B8927C] mb-2">
                    <span>Trend flexibility</span>
                    <span className="font-semibold text-[#D47B5E]">{formData.sustainabilityPriority[0]}</span>
                    <span>Longevity prioritised</span>
                  </div>
                  <Slider
                    min={1}
                    max={5}
                    step={1}
                    value={formData.sustainabilityPriority}
                    onValueChange={handleSustainabilityChange}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-[#B8927C]">
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                  </div>
                </div>

                {/* Climate Context */}
                <div className="space-y-4">
                  <Label htmlFor="climate" className="text-lg font-semibold text-[#D47B5E]">
                    Climate Context *
                  </Label>
                  <Select value={formData.climateContext} onValueChange={handleClimateChange}>
                    <SelectTrigger className="border-[#E8C4AD]">
                      <SelectValue placeholder="Select climate context" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="uk-winter">UK winter</SelectItem>
                      <SelectItem value="uk-spring-autumn">UK spring/autumn</SelectItem>
                      <SelectItem value="uk-summer">UK summer</SelectItem>
                      <SelectItem value="indoor-formal">Indoor formal event</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Communication Goal */}
                <div className="space-y-4">
                  <Label htmlFor="communication" className="text-lg font-semibold text-[#D47B5E]">
                    What do you want this outfit to communicate? *
                  </Label>
                  <Textarea
                    id="communication"
                    rows={5}
                    value={formData.communicationGoal}
                    onChange={handleCommunicationChange}
                    placeholder="Describe the message, feeling, or impression you want to convey through your outfit..."
                    className="border-[#E8C4AD] focus:ring-[#E8B298]"
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    className="w-full bg-[#D47B5E] hover:bg-[#B8614A] text-white py-6 text-lg"
                  >
                    Generate Design Brief
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Demo;
